import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import { ProductCard, ComingSoonCard } from "@/components/ProductCard";
import { company } from "@/data/company";
import { products, upcomingPlaceholder } from "@/data/products";

function SectionLabel({ children }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
      {children}
    </span>
  );
}

function Icon({ name, className }) {
  const C = Icons[name] || Sparkles;
  return <C className={className} />;
}

export default function Home() {
  return (
    <>
      <SEO
        title="AI-powered apps that make learning smarter"
        description={company.intro}
        path="/"
      />

      {/* Hero */}
      <section className="relative overflow-hidden gradient-emerald">
        <div className="grain absolute inset-0 opacity-40" />
        <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-primary/15 blur-3xl" />
        <div className="pointer-events-none absolute right-0 top-40 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-5 pb-24 pt-36 sm:px-8 md:pt-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-1.5 text-sm text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Independent AI software studio
            </span>
            <h1 className="mt-6 font-display text-5xl font-light leading-none tracking-tighter md:text-6xl lg:text-7xl">
              Building AI-powered apps that make{" "}
              <span className="text-primary">learning smarter.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              {company.intro}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-full px-7" data-testid="hero-explore-products">
                <Link to="/products">
                  Explore Products
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full px-7"
                data-testid="hero-contact-us"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-border bg-card/40">
        <Stagger className="mx-auto grid max-w-6xl grid-cols-2 gap-px px-5 sm:px-8 lg:grid-cols-4">
          {company.stats.map((s) => (
            <motion.div
              key={s.label}
              variants={staggerItem}
              className="px-4 py-10 text-center lg:text-left"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="font-display text-3xl font-medium tracking-tight text-foreground md:text-4xl">
                {s.value}
              </div>
              <div className="mt-2 text-sm font-medium text-foreground">{s.label}</div>
              <div className="mt-1 text-xs text-muted-foreground">{s.note}</div>
            </motion.div>
          ))}
        </Stagger>
      </section>

      {/* Mission */}
      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <SectionLabel>Our mission</SectionLabel>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-8">
            <p className="font-display text-2xl font-light leading-snug tracking-tight md:text-4xl">
              {company.mission}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Core values */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
          <Reveal>
            <SectionLabel>Core values</SectionLabel>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-light tracking-tight md:text-5xl">
              Principles behind everything we build.
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {company.values.map((v) => (
              <motion.div
                key={v.title}
                variants={staggerItem}
                whileHover={{ y: -4 }}
                className="rounded-2xl border border-border bg-card p-7"
                data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10">
                  <Icon name={v.icon} className="h-5 w-5 text-primary" />
                </div>
                <h3 className="mt-5 font-display text-lg font-medium tracking-tight">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.description}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Flagship product preview */}
      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
        <Reveal>
          <SectionLabel>Our products</SectionLabel>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <h2 className="max-w-2xl font-display text-3xl font-light tracking-tight md:text-5xl">
              A growing family of AI apps.
            </h2>
            <Link
              to="/products"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              data-testid="home-view-all-products"
            >
              View all products
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <ProductCard product={products[0]} featured />
        </Reveal>
        <Reveal delay={0.15} className="mt-6">
          <ComingSoonCard
            title={upcomingPlaceholder.title}
            description={upcomingPlaceholder.description}
          />
        </Reveal>
      </section>

      {/* Why choose */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
          <Reveal>
            <SectionLabel>Why Envago Labs</SectionLabel>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-light tracking-tight md:text-5xl">
              Software built to be trusted.
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-6 md:grid-cols-2">
            {company.whyChoose.map((w) => (
              <motion.div
                key={w.title}
                variants={staggerItem}
                className="flex gap-5 rounded-2xl border border-border bg-card p-7"
                data-testid={`why-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gold/15">
                  <Icon name={w.icon} className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-medium tracking-tight">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {w.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
        <Reveal className="relative overflow-hidden rounded-3xl border border-border bg-primary px-8 py-16 text-center text-primary-foreground md:py-20">
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <h2 className="relative mx-auto max-w-2xl font-display text-3xl font-light tracking-tight md:text-4xl">
            Let's build something meaningful together.
          </h2>
          <p className="relative mx-auto mt-4 max-w-xl text-base text-primary-foreground/80">
            Questions, partnerships or feedback — we'd love to hear from you.
          </p>
          <Button
            asChild
            size="lg"
            variant="secondary"
            className="relative mt-8 rounded-full px-7"
            data-testid="cta-contact-button"
          >
            <Link to="/contact">
              Get in touch
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </Reveal>
      </section>
    </>
  );
}
